import React from 'react';
import { HiExclamationTriangle } from 'react-icons/hi2';
import Portal  from '../../../../components/Portal';

interface DeleteTaskModalProps {
  deleteConfirmModalShow: boolean;
  unassignedTasksLoading: boolean;
  groupAssignedTask: any;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
  onCloseDeleteModal: () => void;
}

const DeleteTaskModal: React.FC<DeleteTaskModalProps> = ({
  deleteConfirmModalShow,
  unassignedTasksLoading,
  groupAssignedTask,
  onDeleteConfirm,
  onDeleteCancel,
  onCloseDeleteModal,
}) => {
  const handleBackdropClose = () => {
    if (!unassignedTasksLoading) {
      onCloseDeleteModal();
    }
  };

  if (!deleteConfirmModalShow) return null;

  return (
    <Portal>
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
        onClick={handleBackdropClose}
        aria-label="Close modal"
        disabled={unassignedTasksLoading}
        tabIndex={unassignedTasksLoading ? -1 : 0}
      />

      {/* Modal */}
      <dialog
        open
        aria-modal="true"
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 mx-4 max-w-md w-full transform transition-all"
      >
        {/* Icon */}
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/30 rounded-full">
          <HiExclamationTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
        </div>

        {/* Title */}
        <h3 id="delete-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
          Remove work item assignment
        </h3>

        {/* Description */}
        <p id="delete-modal-description" className="text-sm text-gray-600 dark:text-gray-300 text-center mb-6">
          {unassignedTasksLoading ? (
            <span className="inline-flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Removing assignment for "{groupAssignedTask?.task_title ?? 'this work item'}"...
            </span>
          ) : (
            <>
              Are you sure you want to remove the assignment for {groupAssignedTask?.task_title ? (
                <span className="font-medium text-gray-900 dark:text-gray-100">"{groupAssignedTask.task_title}"</span>
              ) : (
                'this work item'
              )}? This action cannot be undone.
            </>
          )}
        </p>

        {/* Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onDeleteCancel}
            disabled={unassignedTasksLoading}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              unassignedTasksLoading
                ? 'text-gray-400 bg-gray-100 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
                : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:focus:ring-gray-400'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={onDeleteConfirm}
            disabled={unassignedTasksLoading}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 flex items-center justify-center min-w-[100px] ${
              unassignedTasksLoading
                ? 'bg-red-600 cursor-not-allowed opacity-80'
                : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 dark:focus:ring-red-400 opacity-100'
            }`}
          >
            {unassignedTasksLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-white">Removing...</span>
              </>
            ) : (
              'Remove'
            )}
          </button>
        </div>
      </dialog>
    </div>
    </Portal>
  );
};

export default DeleteTaskModal; 